/**
 * Medicines from the routine's side — the owner's request after build 540,
 * 4 October 2026.
 *
 * A medicine was tied to a meal or one of the person's own times only from
 * the medicine (Medicines → change its times → Pick the times). The owner
 * expected the routine screen to say what is taken at each time, and to tie
 * one there too. This is the arithmetic of that, shared by the phone's
 * Daily routine and the website's Routine, so the two cannot drift:
 *
 *  - **What is tied where** (`linksAt`): the schedule in force today, its
 *    anchors at one time, each said as before / with / after.
 *  - **Linking** (`planLink`): one more anchor on the medicine's schedule —
 *    or a different before / with / after on the one there — and how many
 *    times a day it is taken before and after, so the screen can say so
 *    before anything is saved.
 *  - **Unlinking** (`planUnlink`): the anchor gone. Never the last one: a
 *    medicine with no times would never ring, so the screen offers to stop
 *    it instead.
 *
 * Saving is the medicine's own change of times, `PUT …/schedule`, with the
 * body built here (`changeBody`): the same route, the same validation, the
 * same moved-dose rules and the same statement line as the change made from
 * the medicine. There is no second way to change a schedule.
 *
 * The types are the shape `GET /patients/:id/medications` answers, as far
 * as this reads it, so each app passes its own list unchanged.
 */
import { buildNotation, FOOD_OFFSET_MINUTES, type BuiltSlots } from './notation.js';
import { MEAL_KEYS, type AnchorKey, type FoodRelation, type MealAnchor, type MealKey } from './types.js';

export type Relation = 'before' | 'with' | 'after';

/** One schedule row of a medicine, as the medicines list sends it. */
export interface LinkSchedule {
  type: 'fixed_times' | 'meal_relative' | 'interval' | 'prn';
  anchors?: MealAnchor[] | null;
  foodRelation: string | null;
  doseQuantity?: number;
  doseUnit?: string;
  daysOfWeek?: number[] | null;
  /** The course, in the person's own calendar; the end is inclusive. */
  startDate: string;
  endDate: string | null;
  archivedAt: number | null;
}

/** One medicine and its schedules, as the medicines list sends it. */
export interface LinkEntry<S extends LinkSchedule = LinkSchedule> {
  medication: { id: string; name: string; strength: string | null; strengthUnit: string | null };
  schedules: S[];
}

/** The server's limit on a schedule's anchors (medications.ts `scheduleInput`). */
export const MAX_ANCHORS = 6;

/** The meals' own offsets (notation.ts): before is half an hour before, after half an hour after. */
export const RELATION_OFFSET: Record<Relation, number> = {
  before: FOOD_OFFSET_MINUTES.before_food,
  with: FOOD_OFFSET_MINUTES.with_food,
  after: FOOD_OFFSET_MINUTES.after_food,
};

export function relationOf(offsetMinutes: number): Relation {
  return offsetMinutes < 0 ? 'before' : offsetMinutes > 0 ? 'after' : 'with';
}

/**
 * The choices a time offers. Bedtime is "at bedtime" and nothing else, as
 * everywhere the app reads a prescription: nobody is told to take a tablet
 * half an hour after going to bed.
 */
export function relationsFor(key: AnchorKey): readonly Relation[] {
  return key === 'bedtime' ? ['with'] : ['before', 'with', 'after'];
}

/** The medicine's name as the list shows it: as entered, with its strength. */
export function medicineTitle(m: LinkEntry['medication']): string {
  return [m.name, m.strength, m.strengthUnit].filter(Boolean).join(' ');
}

export const latestFirst = <T extends Pick<LinkSchedule, 'startDate'>>(rows: readonly T[]): T[] =>
  [...rows].sort((a, b) => b.startDate.localeCompare(a.startDate));

/** The instruction in force today: started, not ended, not archived. The latest to start, if somehow two. */
export function scheduleInForce<S extends LinkSchedule>(entry: LinkEntry<S>, today: string): S | null {
  return latestFirst(entry.schedules
    .filter((s) => !s.archivedAt && s.startDate <= today && (s.endDate === null || s.endDate >= today)))[0] ?? null;
}

/**
 * The instructions the medicine list says: in force today, or waiting to
 * start. Not the earlier ones a change of times closed — those are history,
 * and a card reading "1-0-1 · 1-1-1" names two instructions as if both
 * rang. A course already over still shows its last one.
 */
export function shownSchedules<S extends LinkSchedule>(entry: LinkEntry<S>, today: string): S[] {
  const live = entry.schedules.filter((sc) => !sc.archivedAt);
  const current = live.filter((sc) => sc.endDate === null || sc.endDate >= today);
  return (current.length > 0 ? current : latestFirst(live).slice(0, 1))
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
}

/** A change of times already waiting to start: today's change would set it aside, so it is made from the medicine. */
function changeWaiting(entry: LinkEntry, today: string): boolean {
  return entry.schedules.some((s) => !s.archivedAt && s.startDate > today);
}

/** Anything still taken, or about to be: the medicines a time offers to link. A course already over is not. */
export function linkableMedicines<E extends LinkEntry>(entries: readonly E[], today: string): E[] {
  return entries
    .filter((e) => scheduleInForce(e, today) || e.schedules.some((sc) => !sc.archivedAt && sc.startDate > today))
    .sort((a, b) => medicineTitle(a.medication).localeCompare(medicineTitle(b.medication)));
}

export interface TimeLink {
  medicationId: string;
  title: string;
  relation: Relation;
  /** Tablets (or the medicine's unit) at this time. */
  quantity: number;
}

/** Everything taken at one time, by name. */
export function linksAt(entries: readonly LinkEntry[], key: AnchorKey, today: string): TimeLink[] {
  const out: TimeLink[] = [];
  for (const entry of entries) {
    const s = scheduleInForce(entry, today);
    if (!s || s.type !== 'meal_relative') continue;
    for (const a of s.anchors ?? []) {
      if (a.meal !== key) continue;
      out.push({
        medicationId: entry.medication.id,
        title: medicineTitle(entry.medication),
        relation: relationOf(a.offsetMinutes),
        quantity: a.doseQuantity ?? s.doseQuantity ?? 1,
      });
    }
  }
  return out.sort((a, b) => a.title.localeCompare(b.title));
}

/** Why a medicine cannot be tied to a time from here. */
export type LinkRefusal =
  /** Clock times, every few hours, or as needed: not tied to the day's times at all. */
  | 'not_meal'
  /** A change of times is waiting to start; make the next one from the medicine. */
  | 'change_waiting'
  /** Already at six times a day, the most a schedule holds. */
  | 'too_many'
  /** Nothing in force today (a course over). */
  | 'not_running';

/** Whether a medicine can be linked at all, before a time or a before / after is chosen. */
export function linkRefusal(entry: LinkEntry, today: string): Exclude<LinkRefusal, 'too_many'> | null {
  const s = scheduleInForce(entry, today);
  if (!s) return 'not_running';
  if (changeWaiting(entry, today)) return 'change_waiting';
  if (s.type !== 'meal_relative') return 'not_meal';
  return null;
}

/** Exactly what `PUT /patients/:id/medications/:id/schedule` takes — the medicine screen's change of times. */
export interface ChangeBody {
  schedule: { type: 'meal_relative'; anchors: MealAnchor[] };
  doseQuantity: number;
  doseUnit: string;
  foodRelation: FoodRelation;
  daysOfWeek: number[] | null;
  effectiveFrom: string;
  sourceNotation?: string;
}

export type LinkPlan =
  | { ok: false; reason: LinkRefusal }
  | {
      ok: true;
      /** A new time; a different before / with / after at a time it had; or exactly as it is. */
      kind: 'add' | 'change' | 'same';
      /** Times a day, now and after saving. */
      timesBefore: number;
      timesAfter: number;
      /** The anchor at this time after saving, to say it back ("1 tablet 30 min after lunch"). */
      anchor: MealAnchor;
      body: ChangeBody;
    };

export function planLink(entry: LinkEntry, key: AnchorKey, relation: Relation, today: string): LinkPlan {
  const refused = linkRefusal(entry, today);
  if (refused) return { ok: false, reason: refused };
  const s = scheduleInForce(entry, today)!;
  const anchors = [...(s.anchors ?? [])];
  const offsetMinutes = key === 'bedtime' ? 0 : RELATION_OFFSET[relation];
  const here = anchors.filter((a) => a.meal === key);

  if (here.length > 0) {
    const same = here.every((a) => a.offsetMinutes === offsetMinutes);
    const next = same ? anchors : anchors.map((a) => (a.meal === key ? { ...a, offsetMinutes } : a));
    return {
      ok: true,
      kind: same ? 'same' : 'change',
      timesBefore: anchors.length,
      timesAfter: next.length,
      anchor: next.find((a) => a.meal === key)!,
      body: changeBody(s, next, today),
    };
  }

  if (anchors.length >= MAX_ANCHORS) return { ok: false, reason: 'too_many' };
  const anchor: MealAnchor = { meal: key, offsetMinutes, doseQuantity: sharedQuantity(s) };
  const next = [...anchors, anchor];
  return { ok: true, kind: 'add', timesBefore: anchors.length, timesAfter: next.length, anchor, body: changeBody(s, next, today) };
}

export type UnlinkPlan =
  | { ok: false; reason: Exclude<LinkRefusal, 'too_many'> | 'last' | 'not_linked' }
  | { ok: true; timesBefore: number; timesAfter: number; body: ChangeBody };

export function planUnlink(entry: LinkEntry, key: AnchorKey, today: string): UnlinkPlan {
  const refused = linkRefusal(entry, today);
  if (refused) return { ok: false, reason: refused };
  const s = scheduleInForce(entry, today)!;
  const anchors = s.anchors ?? [];
  const next = anchors.filter((a) => a.meal !== key);
  if (next.length === anchors.length) return { ok: false, reason: 'not_linked' };
  // Never a medicine with no times: it would never ring, and nobody would know.
  if (next.length === 0) return { ok: false, reason: 'last' };
  return { ok: true, timesBefore: anchors.length, timesAfter: next.length, body: changeBody(s, next, today) };
}

/**
 * How many to take at a new time: what it is at every other time, when
 * that is one number ("1-0-1" → 1); otherwise the schedule's own.
 */
function sharedQuantity(s: LinkSchedule): number {
  const base = s.doseQuantity ?? 1;
  const quantities = new Set((s.anchors ?? []).map((a) => a.doseQuantity ?? base));
  return quantities.size === 1 ? [...quantities][0]! : base;
}

/**
 * The body for the change of times, from today. Everything the medicine
 * already had — how much, the unit, the days of the week — carried as it
 * was; only the anchors differ.
 */
export function changeBody(s: LinkSchedule, anchors: MealAnchor[], today: string): ChangeBody {
  const doseQuantity = s.doseQuantity ?? 1;
  const foodRelation = foodFor(anchors, (s.foodRelation ?? 'any') as FoodRelation);
  const notation = notationFor(anchors, foodRelation, doseQuantity);
  return {
    schedule: { type: 'meal_relative', anchors },
    doseQuantity,
    doseUnit: s.doseUnit ?? 'tablet',
    foodRelation,
    daysOfWeek: s.daysOfWeek ?? null,
    effectiveFrom: today,
    ...(notation ? { sourceNotation: notation } : {}),
  };
}

/**
 * The medicine's "after food" (what a reminder says beside its name): the
 * one every timed dose shares, or none when they differ — a reminder saying
 * "after food" for a dose taken before lunch would be wrong.
 */
export function foodFor(anchors: readonly MealAnchor[], previous: FoodRelation): FoodRelation {
  const timed = anchors.filter((a) => a.meal !== 'bedtime');
  if (timed.length === 0) return previous;
  const offsets = new Set(timed.map((a) => a.offsetMinutes));
  if (offsets.size > 1) return 'any';
  const offset = [...offsets][0]!;
  // "Any" and "with food" are both at the meal: the person's own word stays.
  if (FOOD_OFFSET_MINUTES[previous] === offset) return previous;
  const found = (Object.keys(FOOD_OFFSET_MINUTES) as FoodRelation[]).find(
    (f) => f !== 'any' && FOOD_OFFSET_MINUTES[f] === offset,
  );
  return found ?? 'any';
}

/**
 * The prescription shorthand for the meals, when there is one ("1-1-1
 * A/F"), so the medicine list and the statement read as they always have.
 * Undefined when no shorthand says it — before breakfast but after lunch —
 * and the list then says each time in words. Own times are never in a
 * shorthand; the list names them beside it, as it already does.
 */
export function notationFor(anchors: readonly MealAnchor[], food: FoodRelation, quantity: number): string | undefined {
  const meals = anchors.filter((a): a is MealAnchor & { meal: MealKey } => (MEAL_KEYS as readonly string[]).includes(a.meal));
  if (meals.length === 0) return undefined;
  if (new Set(meals.map((a) => a.meal)).size !== meals.length) return undefined;
  const slots: BuiltSlots = {};
  for (const a of meals) {
    const expected = a.meal === 'bedtime' ? 0 : FOOD_OFFSET_MINUTES[food];
    if (a.offsetMinutes !== expected) return undefined;
    slots[a.meal] = a.doseQuantity ?? quantity;
  }
  return buildNotation(slots, food);
}
